import './polyfills';
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { GlobalStyles, ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import theme from './constants/theme';
import Layout from './components/Global/Layout';
import { SURFACE_DEFAULTS } from './constants/surface';
import PageLoading from './components/Global/PageLoading';
import AppErrorFallback from './components/Global/AppErrorFallback';
import lazyWithReload from './utils/lazyWithReload';

// Lazy load all route components
const ShopPage = lazyWithReload(() => import('./routes/shop'));
const ShopServicesPage = lazyWithReload(() => import('./routes/shopServices'));
const ShopServiceBookPage = lazyWithReload(
  () => import('./routes/shopServiceBook'),
);
const ProfilePage = lazyWithReload(() => import('./routes/profile'));
const ArtistPage = lazyWithReload(() => import('./routes/artist'));
const ArtistPreviewPage = lazyWithReload(
  () => import('./routes/artistPreview'),
);
const BookingPage = lazyWithReload(() => import('./routes/booking'));
const ReschedulePage = lazyWithReload(() => import('./routes/reschedule'));
const ChatPage = lazyWithReload(() => import('./routes/chat'));
const GeneralRequest = lazyWithReload(() => import('./routes/generalRequest'));
const ReviewPage = lazyWithReload(() => import('./routes/review'));
const PreviewWaiverPage = lazyWithReload(() => import('./routes/waiver'));
const Service = lazyWithReload(() => import('./routes/service'));
const UnsubscribePage = lazyWithReload(() => import('./routes/unsubscribe'));
const NotFound = lazyWithReload(() => import('./routes/404'));
const CheckoutPage = lazyWithReload(() => import('./routes/checkout'));
const ReceiptPage = lazyWithReload(() => import('./routes/receipt'));
const WaiverSessionPage = lazyWithReload(
  () => import('./routes/waiverSessionPage'),
);
const SubscribeListPage = lazyWithReload(
  () => import('./routes/subscribeList'),
);
const SubscribeForm = lazyWithReload(
  () => import('./components/marketing/SubscribeForm'),
);
const PaymentRedirectPage = lazyWithReload(
  () => import('./routes/paymentRedirect'),
);
const WalkInCheckInPage = lazyWithReload(
  () => import('./routes/walkInCheckIn'),
);
const ArtistFlashbookPage = lazyWithReload(
  () => import('./routes/artistFlashbook'),
);
const FlashDesignPage = lazyWithReload(() => import('./routes/flashDesign'));
const FlashDesignBookingPage = lazyWithReload(
  () => import('./routes/flashDesignBooking'),
);
const ShopFlashbookPage = lazyWithReload(
  () => import('./routes/shopFlashbook'),
);
const ShopFlashDesignPage = lazyWithReload(
  () => import('./routes/shopFlashDesign'),
);
const ShopFlashDesignBookingPage = lazyWithReload(
  () => import('./routes/shopFlashDesignBooking'),
);
const GiftCardBalancePage = lazyWithReload(
  () => import('./routes/giftCardBalance'),
);
const GiftCardPage = lazyWithReload(() => import('./routes/giftCard'));
const GiftCardSharePage = lazyWithReload(
  () => import('./routes/giftCardShare'),
);
const GiftCardStorePage = lazyWithReload(
  () => import('./routes/giftCardStore'),
);
const GiftCardStoreForm = lazyWithReload(
  () => import('./components/gift-cards/GiftCardStoreForm'),
);
const MyGiftCardsPage = lazyWithReload(() => import('./routes/myGiftCards'));
const GiftCardEmbedDemoPage = lazyWithReload(
  () => import('./routes/giftCardEmbedDemo'),
);

const root = createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      {/* Unthemed defaults for every page. Layout overrides them on html on
          booking site pages once the theme resolves. */}
      <GlobalStyles styles={{ ':root': SURFACE_DEFAULTS }} />
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              <Sentry.ErrorBoundary fallback={<AppErrorFallback />}>
                <App />
              </Sentry.ErrorBoundary>
            }>
            <Route element={<Layout />}>
              <Route
                path="/"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <BookingPage />
                  </Suspense>
                }
              />
              <Route
                path="artist/:urlPath"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ArtistPage />
                  </Suspense>
                }
              />
              <Route
                path="artist/:urlPath/preview"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ArtistPreviewPage />
                  </Suspense>
                }
              />
              <Route
                path="artist/:urlPath/subscribe"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <SubscribeListPage />
                  </Suspense>
                }
              />
              <Route
                path="artist/:urlPath/subscribe/embed"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <SubscribeForm />
                  </Suspense>
                }
              />
              <Route
                path="artist/:urlPath/flash"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ArtistFlashbookPage />
                  </Suspense>
                }
              />
              <Route
                path="artist/:urlPath/flash/:flashDesignExternalId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <FlashDesignPage />
                  </Suspense>
                }
              />
              <Route
                path="artist/:urlPath/flash/:flashDesignExternalId/:size"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <FlashDesignBookingPage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ShopPage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/flash"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ShopFlashbookPage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/flash/:flashDesignExternalId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ShopFlashDesignPage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/flash/:flashDesignExternalId/:size"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ShopFlashDesignBookingPage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/:artistUrl"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/request"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <GeneralRequest />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/subscribe"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <SubscribeListPage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/subscribe/embed"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <SubscribeForm />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/gift-cards"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <GiftCardStorePage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/gift-cards/embed"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <GiftCardStoreForm embed />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/gift-cards/embed-demo"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <GiftCardEmbedDemoPage />
                  </Suspense>
                }
              />
              <Route
                path="/shop/:shopUrl/my-gift-cards"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <MyGiftCardsPage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/services"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ShopServicesPage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/service/:serviceId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <Service />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/service/:serviceId/book"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ShopServiceBookPage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/preview-waiver"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <PreviewWaiverPage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/waiver-session/:sessionId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <WaiverSessionPage />
                  </Suspense>
                }
              />
              <Route
                path="shop/:shopUrl/check-in"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <WalkInCheckInPage />
                  </Suspense>
                }
              />
              <Route
                path="/review/:reviewLinkId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ReviewPage />
                  </Suspense>
                }
              />
              <Route
                path="booking/:appointmentId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <BookingPage />
                  </Suspense>
                }
              />
              <Route
                path="booking/"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <BookingPage />
                  </Suspense>
                }
              />
              <Route
                path="reschedule/:appointmentId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ReschedulePage />
                  </Suspense>
                }
              />
              <Route
                path="chat"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ChatPage />
                  </Suspense>
                }
              />
              <Route
                path="chat/:conversationId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ChatPage />
                  </Suspense>
                }
              />
              <Route
                path="unsubscribe"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <UnsubscribePage />
                  </Suspense>
                }
              />
              <Route
                path="/checkout"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <CheckoutPage />
                  </Suspense>
                }
              />
              <Route
                path="/checkout/:paymentId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <CheckoutPage />
                  </Suspense>
                }
              />
              <Route
                path="/receipt/:paymentId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ReceiptPage />
                  </Suspense>
                }
              />
              <Route
                path="/payment-redirect"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <PaymentRedirectPage />
                  </Suspense>
                }
              />
              <Route
                path="/giftcard"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <GiftCardBalancePage />
                  </Suspense>
                }
              />
              <Route
                path="/giftcard/:giftCardId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <GiftCardPage />
                  </Suspense>
                }
              />
              <Route
                path="/giftcard/:giftCardId/share"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <GiftCardSharePage />
                  </Suspense>
                }
              />
              <Route
                path="*"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <NotFound />
                  </Suspense>
                }
              />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  </React.StrictMode>,
);

reportWebVitals();
